import * as React from "react";
import { Link as RouterLink } from "react-router";

import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import type { Theme } from "@mui/material/styles";

import type {
    DuprProfileDto,
    GoalProgressJson,
    Rating,
    UserAtAGlance,
} from "@volley/data";
import { formatDuprRating } from "@volley/data/predicted-rating";

import StatsAchievementIcon from "../Stats/StatsAchievementIcon/StatsAchievementIcon";
import type { Sport } from "../common/context/sport";

interface StatusStripProps {
    atAGlance: UserAtAGlance;
    goalData: GoalProgressJson | null;
    sport: Sport;
    duprProfile: DuprProfileDto | null;
    /** True while the DUPR profile fetch is in flight (pickleball only). */
    duprLoading: boolean;
    /**
     * The player's PTI (platform tennis only). Its own request now, rather than
     * a field on `atAGlance` — see HomePtiContext.
     */
    pti: Rating | null;
    /** True while the PTI fetch is in flight (platform tennis only). */
    ptiLoading: boolean;
    onConnectDupr: () => void;
}

interface StatusItemProps {
    /** Navigate to this route when clicked. Omit when using `onClick`. */
    to?: string;
    /** Act as a button (e.g. to open a dialog) instead of navigating. */
    onClick?: () => void;
    children: React.ReactNode;
}

function StatusItem({
    to,
    onClick,
    children,
}: StatusItemProps): React.JSX.Element {
    const variantProps = to
        ? ({ component: RouterLink, to } as const)
        : ({ component: "button", type: "button", onClick } as const);
    return (
        <Link
            {...variantProps}
            sx={{
                flex: 1,
                textAlign: "center",
                color: "white",
                fontSize: "0.8rem",
                fontWeight: 600,
                fontFamily: "inherit",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                textDecoration: "none",
                border: "none",
                background: "none",
                padding: 0,
                "&:hover": {
                    textDecoration: "underline",
                },
            }}
        >
            {children}
        </Link>
    );
}

interface DuprRatingPanel {
    /** Format label shown as the second line, e.g. "Doubles". */
    label: string;
    /** Rating value, or "NR" when the account is linked but not yet rated. */
    value: string;
}

/**
 * The DUPR rating panels to rotate through for a linked account: one per rated
 * format (Doubles/Singles), ordered with DUPR's primary format (`defaultRating`)
 * first. When linked but unrated, returns a single "NR" panel so the user still
 * sees their link state (confirming the pairing) rather than the connect prompt.
 */
function duprRatingsToShow(profile: DuprProfileDto): DuprRatingPanel[] {
    const formats = [
        { label: "Doubles", key: "DOUBLES", value: profile.doubles?.value },
        { label: "Singles", key: "SINGLES", value: profile.singles?.value },
    ];
    const rated = formats.filter(
        (f): f is { label: string; key: string; value: string } =>
            !!f.value && f.value !== "NR",
    );
    if (rated.length > 0) {
        // Primary format first so the user's main rating leads the rotation.
        rated.sort((a, b) =>
            a.key === profile.defaultRating
                ? -1
                : b.key === profile.defaultRating
                  ? 1
                  : 0,
        );
        return rated.map(({ label, value }) => ({ label, value }));
    }
    const primaryLabel =
        profile.defaultRating === "SINGLES" ? "Singles" : "Doubles";
    return [{ label: primaryLabel, value: "NR" }];
}

function StatusDivider(): React.JSX.Element {
    return (
        <Box
            sx={{
                width: "3px",
                height: "20px",
                backgroundColor: "#2c3e50",
                flexShrink: 0,
            }}
        />
    );
}

function PtiTrendIcon({
    trend,
    ...props
}: { trend: -1 | 0 | 1 } & SvgIconProps): React.JSX.Element | null {
    switch (trend) {
        case -1:
            return <TrendingDownIcon {...props} />;
        case 0:
            return null;
        case 1:
        default:
            return (
                <TrendingUpIcon
                    {...props}
                    sx={{
                        color: (theme) =>
                            `${theme.palette.success.main} !important`,
                        ...props.sx,
                    }}
                />
            );
    }
}

const ratingChipSx = (theme: Theme) => ({
    px: 1,
    textDecoration: "none",
    background: theme.palette.background.default,
    color: theme.palette.text.primary,
});

/** A DUPR rating chip with the format (Singles/Doubles) as a second line. */
function DuprRatingItem({ label, value }: DuprRatingPanel): React.JSX.Element {
    return (
        <StatusItem to="/dupr-dashboard">
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "2px",
                }}
            >
                <Chip
                    size="small"
                    label={`DUPR ${formatDuprRating(value)}`}
                    sx={ratingChipSx}
                />
                <Typography
                    component="span"
                    sx={{ fontSize: "0.65rem", fontWeight: 600, lineHeight: 1 }}
                >
                    {label}
                </Typography>
            </Box>
        </StatusItem>
    );
}

export default function StatusStrip({
    atAGlance,
    goalData,
    sport,
    duprProfile,
    duprLoading,
    pti,
    ptiLoading,
    onConnectDupr,
}: StatusStripProps): React.JSX.Element {
    const goalProgress = React.useMemo(() => {
        if (goalData?.goal) {
            const target = parseInt(goalData.goal.target, 10);
            return Math.round((goalData.actual / target) * 100);
        }
        return null;
    }, [goalData]);

    const volleyLevelContent = (
        <StatusItem to="/stats/achievements">
            <StatsAchievementIcon
                achievementId={atAGlance.activityAchievement.id}
                fontSize="small"
            />
            <Typography
                component="span"
                sx={{ fontSize: "inherit", fontWeight: "inherit" }}
            >
                {atAGlance.activityAchievement.name}
            </Typography>
        </StatusItem>
    );
    const ptiContent =
        !ptiLoading && pti?.value != null ? (
            <StatusItem to="/player-pti/summary">
                <Chip
                    size="small"
                    label={
                        <>
                            {"PTI "}
                            {pti.value}{" "}
                            <PtiTrendIcon
                                trend={pti.trend ?? 0}
                                sx={{
                                    fontSize: "1.2rem",
                                    verticalAlign: "middle",
                                }}
                            />
                        </>
                    }
                    sx={ratingChipSx}
                />
            </StatusItem>
        ) : null;
    const connectContent = (
        <StatusItem onClick={onConnectDupr}>Connect DUPR</StatusItem>
    );

    // A single static panel. Pickleball surfaces DUPR only — the linked rating
    // (primary format) or the connect prompt, never the Volley level; nothing
    // while the DUPR fetch is in flight. Platform tennis shows PTI; all other
    // cases fall back to the Volley level. (This cell used to rotate through
    // every rating and the Volley level for users off the DUPR flow.)
    let middleContent: React.ReactNode = null;
    if (sport === "PICKLEBALL") {
        if (!duprLoading) {
            middleContent = duprProfile ? (
                <DuprRatingItem {...duprRatingsToShow(duprProfile)[0]} />
            ) : (
                connectContent
            );
        }
    } else if (sport === "PLATFORM_TENNIS" && ptiContent) {
        middleContent = ptiContent;
    } else {
        middleContent = volleyLevelContent;
    }

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "transparent",
                border: "2px solid #2c3e50",
                borderRadius: "8px",
                marginTop: "15px",
                padding: "10px 5px",
            }}
        >
            <StatusItem to="/stats/goal">
                {goalProgress !== null ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                        }}
                    >
                        {goalProgress > 0 && (
                            <Box sx={{ position: "relative", display: "flex" }}>
                                <CircularProgress
                                    variant="determinate"
                                    value={100}
                                    size={24}
                                    sx={{ color: "rgba(255, 255, 255, 0.2)" }}
                                />
                                <CircularProgress
                                    variant="determinate"
                                    value={Math.min(goalProgress, 100)}
                                    size={24}
                                    sx={{
                                        color: "#4ade80",
                                        position: "absolute",
                                        left: 0,
                                    }}
                                />
                            </Box>
                        )}
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                            }}
                        >
                            <span>{goalProgress}%</span>
                            <span>Goal</span>
                        </Box>
                    </Box>
                ) : (
                    "Set Goal"
                )}
            </StatusItem>
            <StatusDivider />
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {middleContent}
            </Box>
            <StatusDivider />
            <StatusItem to="/stats/leaderboard">
                {atAGlance.globalRank > 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <span>
                            #{Intl.NumberFormat().format(atAGlance.globalRank)}
                        </span>
                        <span>Leaderboard</span>
                    </Box>
                ) : (
                    "Leaderboard"
                )}
            </StatusItem>
        </Box>
    );
}
